import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './HelpPage.module.css';

const ScreenshotPlaceholder = ({ alt, src }) =>
  src ? (
    <div className={styles.screenshotWrapper}>
      <img src={src} alt={alt} className={styles.screenshotImg} />
    </div>
  ) : (
    <div className={styles.screenshotPlaceholder} aria-label={alt}>
      <span className={styles.screenshotLabel}>【スクリーンショット】</span>
    </div>
  );

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.faqItem}>
      <button
        type="button"
        className={styles.faqButton}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className={styles.faqQ}>Q. {q}</span>
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>▾</span>
      </button>
      {open && <p className={styles.faqA}>A. {a}</p>}
    </div>
  );
};

const HelpSection = ({ section, stepsLabel, tipsLabel, faqLabel, screenshotAlt }) => (
  <section id={section.id} className={styles.section}>
    <div className={styles.sectionHeader}>
      <span className={styles.stepBadge}>STEP {section.step}</span>
      <h2 className={styles.sectionTitle}>{section.title}</h2>
    </div>

    <p className={styles.overview}>
      {section.overview.split('\n').map((line, i, arr) => (
        <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
      ))}
    </p>

    <ScreenshotPlaceholder alt={screenshotAlt} src={section.screenshot} />

    <div className={styles.block}>
      <h3 className={styles.blockTitle}>{stepsLabel}</h3>
      <ol className={styles.stepsList}>
        {section.steps.map((step, i) => (
          <li key={i} className={styles.stepItem}>
            {typeof step === 'string' ? step : (
              <>
                {step.pre}<a href={step.linkHref} className={styles.stepLink} target="_blank" rel="noopener noreferrer">{step.linkText}</a>{step.post}
              </>
            )}
          </li>
        ))}
      </ol>
    </div>

    {section.tips.length > 0 && (
      <div className={styles.block}>
        <h3 className={styles.blockTitle}>{tipsLabel}</h3>
        <ul className={styles.tipsList}>
          {section.tips.map((tip, i) => (
            <li key={i} className={styles.tipItem}>
              {tip}
            </li>
          ))}
        </ul>
      </div>
    )}

    {section.faq.length > 0 && (
      <div className={styles.block}>
        <h3 className={styles.blockTitle}>{faqLabel}</h3>
        {section.faq.map((item, i) => (
          <FaqItem key={i} q={item.q} a={item.a} />
        ))}
      </div>
    )}
  </section>
);

const HelpPage = () => {
  const { t } = useTranslation('help');

  const sections = t('sections', { returnObjects: true });
  const stepsLabel = t('stepsLabel');
  const tipsLabel = t('tipsLabel');
  const faqLabel = t('faqLabel');
  const screenshotAlt = t('screenshotAlt');

  return (
    <div className={styles.container}>
      {/* Hero */}
      <div className={styles.hero}>
        <h1 className={styles.pageTitle}>{t('pageTitle')}</h1>
        <p className={styles.pageSubtitle}>{t('pageSubtitle')}</p>
      </div>

      <div className={styles.layout}>
        {/* Table of Contents — sticky sidebar on desktop, top bar on mobile */}
        <nav className={styles.toc} aria-label={t('tocTitle')}>
          <p className={styles.tocTitle}>{t('tocTitle')}</p>
          <ol className={styles.tocList}>
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className={styles.tocLink}>
                  <span className={styles.tocStep}>{section.step}</span>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Main content */}
        <main className={styles.main}>
          {sections.map((section) => (
            <HelpSection
              key={section.id}
              section={section}
              stepsLabel={stepsLabel}
              tipsLabel={tipsLabel}
              faqLabel={faqLabel}
              screenshotAlt={screenshotAlt}
            />
          ))}
        </main>
      </div>
    </div>
  );
};

export default HelpPage;
